import { injectable } from 'inversify';

import { t } from '@vk-hr-tek/core/translations/t';

import {
  WidgetPartition,
  WidgetPartitionCreateOption,
} from '@app/gen/dashboard';
import { UserRoleEnum } from '@app/types';

import { GridCard, GridCardSize, PartitionItem } from '../../types';

const PARTITION_WIDTH_SMALL = 1;
const PARTITION_WIDTH_MEDIUM = 2;

@injectable()
export class PartitionMapper {
  private getPartitionSize(width: number) {
    return width === 1 ? 's' : 'm';
  }

  private getPartitionName(partitionKey: string, side: UserRoleEnum): string {
    return t([
      `settings.company.widgets.partitions.${side}.${partitionKey}`,
      `settings.company.widgets.partitions.${partitionKey}`,
    ]);
  }

  private partitionCreateOptionToGridItem(
    option: WidgetPartitionCreateOption,
    side: UserRoleEnum,
  ): GridCard {
    return {
      id: option.type,
      name: this.getPartitionName(option.type, side),
      size: 'S' as GridCardSize,
    };
  }

  processPartitions(partitions: WidgetPartition[], side: UserRoleEnum) {
    return partitions
      .map((item) => this.processPartition(item, side))
      .filter((partition) => !!partition);
  }

  private processPartition(
    partition: WidgetPartition,
    side: UserRoleEnum,
  ): PartitionItem | null {
    return {
      type: partition.type,
      title: this.getPartitionName(partition.type, side),
      size: this.getPartitionSize(partition.width || 1),
    };
  }

  processAvailablePartitions(
    partitions: WidgetPartitionCreateOption[],
    side: UserRoleEnum,
  ) {
    return partitions.map((item) =>
      this.partitionCreateOptionToGridItem(item, side),
    );
  }

  processGridItems(items: GridCard[]) {
    return items.map((item) => this.processGridItem(item));
  }

  private processGridItem(item: GridCard): WidgetPartition {
    return {
      type: item.id as WidgetPartition['type'],
      width: item.size === 'S' ? PARTITION_WIDTH_SMALL : PARTITION_WIDTH_MEDIUM,
    };
  }
}
